import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import { sanitizeInput } from '../../utils/validationUtils';
import { warehouseService as apiWarehouseService } from '../../services/apiServices';

const WarehouseRegister = () => {
  const [formData, setFormData] = useState({
    location: '',
    manager: ''
  });
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [warehouses, setWarehouses] = useState([]);
  const [warehousesLoading, setWarehousesLoading] = useState(false);
  const [warehousesError, setWarehousesError] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);

  // Fetch warehouses on component mount
  useEffect(() => {
    fetchWarehouses();
  }, []);

  const fetchWarehouses = async () => {
    setWarehousesLoading(true);
    setWarehousesError(null);

    try {
      const response = await apiWarehouseService.getWarehouses();
      
      // Handle response based on its structure
      if (response.data && response.data.data) {
        setWarehouses(response.data.data);
      } else if (Array.isArray(response.data)) {
        setWarehouses(response.data);
      } else {
        setWarehouses([]);
      }
    } catch (err) {
      console.error('Error fetching warehouses:', err);
      let errorMessage = 'Failed to fetch warehouses.';
      
      if (err.name === 'TypeError' && err.message.includes('fetch')) {
        // Network error
        errorMessage = 'Network error. Please check your connection.';
      } else {
        errorMessage = err.message || errorMessage;
      }
      
      setWarehousesError(errorMessage);
      setWarehouses([]);
    } finally {
      setWarehousesLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: sanitizeInput(value)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Basic validation
    if (!formData.location.trim() || !formData.manager.trim()) {
      setError('Both Location and Manager are required fields.');
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(false);

    try {
      // Create warehouse using the warehouseService which properly handles authentication
      // Include status as 1 by default when creating a new warehouse
      const warehouseData = {
        ...formData,
        status: 1
      };
      const response = await apiWarehouseService.createWarehouse(warehouseData);

      // Show success message from API response
      const successMessage = response.data?.message
        ? response.data.message
        : 'Warehouse registered successfully!';
      setSuccess(successMessage);

      // Reset form after successful submission
      setFormData({
        location: '',
        manager: ''
      });

      // Refresh the warehouse list after successful creation
      await fetchWarehouses();

      // Hide success message after 3 seconds
      setTimeout(() => {
        setSuccess(false);
      }, 3000);

      // Close the dialog after successful submission
      handleCloseDialog();
    } catch (err) {
      console.error('Error registering warehouse:', err);
      let errorMessage = 'An error occurred while registering the warehouse.';
      
      // Use the error message from the API if available
      if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      setError(errorMessage);
      setSuccess(false);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = () => {
    setFormData({
      location: '',
      manager: ''
    });
    setError(null);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setError(null);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          Warehouses
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={handleOpenDialog}
        >
          Register Warehouse
        </Button>
      </Box>

      {/* Display the list of warehouses */}
      {warehousesLoading && (
        <Box display="flex" justifyContent="center" my={4}>
          <CircularProgress />
        </Box>
      )}

      {warehousesError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {warehousesError}
        </Alert>
      )}

      {!warehousesLoading && !warehousesError && warehouses.length === 0 && (
        <Alert severity="info">No warehouses found.</Alert>
      )}

      {!warehousesLoading && !warehousesError && warehouses.length > 0 && (
        <Paper>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>ID</TableCell>
                  <TableCell>Location</TableCell>
                  <TableCell>Manager</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Created At</TableCell>
                  <TableCell>Updated At</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {warehouses.map((warehouse) => (
                  <TableRow key={warehouse.id}>
                    <TableCell>{warehouse.id}</TableCell>
                    <TableCell>{sanitizeInput(warehouse.location)}</TableCell>
                    <TableCell>{sanitizeInput(warehouse.manager)}</TableCell>
                    <TableCell>
                      <Chip
                        label={warehouse.status === 1 ? 'Active' : 'Inactive'}
                        color={warehouse.status === 1 ? 'success' : 'error'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{warehouse.createdAt ? new Date(warehouse.createdAt).toLocaleDateString() : 'N/A'}</TableCell>
                    <TableCell>{warehouse.updatedAt ? new Date(warehouse.updatedAt).toLocaleDateString() : 'N/A'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Registration Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} fullWidth maxWidth="sm">
        <DialogTitle>Register New Warehouse</DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent dividers>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            <TextField
              autoFocus
              margin="dense"
              name="location"
              label="Location"
              type="text"
              fullWidth
              variant="outlined"
              value={sanitizeInput(formData.location)}
              onChange={handleInputChange}
              required
              sx={{ mb: 2 }}
            />
            <TextField
              margin="dense"
              name="manager"
              label="Manager"
              type="text"
              fullWidth
              variant="outlined"
              value={sanitizeInput(formData.manager)}
              onChange={handleInputChange}
              required
              sx={{ mb: 2 }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Cancel</Button>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={loading}
            >
              {loading ? (
                <>
                  <CircularProgress size={20} sx={{ mr: 1 }} />
                  Registering...
                </>
              ) : (
                'Register'
              )}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {success && typeof success === 'string' ? (
        <Alert severity="success" sx={{ mt: 2 }}>
          {success}
        </Alert>
      ) : success && (
        <Alert severity="success" sx={{ mt: 2 }}>
          Warehouse registered successfully!
        </Alert>
      )}
    </Box>
  );
};

export default WarehouseRegister;